/**
 * Generic "Coming soon" panel surface — the body content for
 * every dockable surface that hasn't shipped its real
 * implementation yet.
 *
 * Stream C PR 1 registered all primary panels per ADR-024 §2;
 * only ``map`` (from Stream A PR 2) had a real content component
 * then. As of the PR5a cleanup (the ``allow-list`` panel was
 * retired) the placeholder still backs the not-yet-shipped surfaces
 * (``onboarding``, ``user-management``) until their owning streams
 * swap in real content. (``system-health`` shipped real content and
 * was superseded by ``system-status`` — Flow L / ADR-035 / S6.)
 *
 * The placeholder still mounts inside the dockview group so:
 *
 * - Layout persistence round-trips correctly (the panel's
 *   position survives reload + save) — REQ-UI-052 / 053.
 * - The Add panel menu (PR 2) can list every dockable surface
 *   role-filtered per REQ-UI-047 without false-positives from
 *   missing components.
 * - The 3 named presets (Map + Platforms, Operations Center) can
 *   reference these panels in the initial layout without crashing
 *   dockview.
 *
 * Cross-references:
 *   - ADR-024 §2 (the dockable surfaces inventory).
 *   - docs/attic/mvp/streams/C-multi-pane.md
 *     §"First-week plan" item 5 (only Map = real in PR 1).
 */
import type { IDockviewPanelProps } from "dockview-react";
import type { PanelId } from "@/multi-pane/registry-types";
import { PANEL_TITLES } from "@/multi-pane/registry-types";

/**
 * Render a dimmed "Coming soon" panel body. Owning streams (A
 * PR 5 / D / Audit-log UI PR / Stream A PR 6) swap a real
 * component in via :func:`panelComponents` when their surface
 * ships.
 *
 * The panel ID is read from ``props.api.id`` (dockview-react's
 * own per-panel identity) rather than ``props.params`` — preset-
 * loaded panels are described in :type:`SerializedDockview`'s
 * ``panels`` map as ``{ id, contentComponent, title }`` without
 * a ``params`` slot, so consumers must rely on ``api.id`` for
 * the panel-identity contract.
 */
export function PlaceholderPanel(props: IDockviewPanelProps): React.ReactElement {
  const id = props.api.id as PanelId;
  const title = PANEL_TITLES[id] ?? id;
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base text-text-secondary"
      data-testid={`panel-placeholder-${id}`}
      role="region"
      aria-label={`${title} — coming soon`}
    >
      <p className="text-h2 text-text-primary">{title}</p>
      <p className="text-body-sm text-text-muted">Coming soon.</p>
    </div>
  );
}
